import { supabase } from '../supabase';
import { 
  Game as IndexGame, 
  UserGame,
  Recommendation,
  RecommendationAlgorithm 
} from '../../types/index';
import { RecommendationDataService } from '../recommenders/services/recommendationDataService';
import { RECOMMENDATION_CONFIG } from '../recommenders';

/**
 * Serviço responsável por calcular e gerenciar as pontuações das recomendações de jogos
 * Este serviço implementa diferentes modelos de pontuação e normalização para melhorar a qualidade das recomendações
 */
export class RecommendationScorer {
  constructor() {}

  /**
   * Calcula a similaridade de cosseno entre dois vetores.
   * Retorna 0 se os vetores forem inválidos ou tiverem norma zero.
   */
  private _calculateCosineSimilarity(vecA: number[] | null, vecB: number[] | null): number {
    if (!vecA || !vecB || vecA.length !== vecB.length || vecA.length === 0) {
      return 0;
    }

    let dotProduct = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < vecA.length; i++) {
      dotProduct += vecA[i] * vecB[i];
      normA += vecA[i] * vecA[i];
      normB += vecB[i] * vecB[i];
    }

    const magnitudeA = Math.sqrt(normA);
    const magnitudeB = Math.sqrt(normB);

    if (magnitudeA === 0 || magnitudeB === 0) {
      return 0; // Evita divisão por zero
    }

    // O cosseno está entre -1 e 1, normalizamos para 0 a 1
    const similarity = dotProduct / (magnitudeA * magnitudeB);
    return (similarity + 1) / 2;
  }

  /**
   * Calcula a pontuação e a razão para uma recomendação.
   * Usa IndexGame e retorna Recommendation de index.ts
   */
  public async scoreGameForUser(
    userId: string,
    game: IndexGame,
    algorithm: RecommendationAlgorithm,
    userCollection: UserGame[] = [],
    userEmbeddingInput?: number[] | null,
    gameEmbeddingInput?: number[] | null
  ): Promise<{ score: number; reason: string }> {
    try {
      // Buscar embeddings se não fornecidos
      let userEmbedding: number[] | null = userEmbeddingInput === undefined ? null : userEmbeddingInput;
      if (userEmbedding === null) { // Buscar apenas se explicitamente null ou undefined inicialmente
        const { data: userEmbData, error: userEmbError } = await supabase
          .from('user_embeddings')
          .select('embedding')
          .eq('user_id', userId)
          .maybeSingle();
        if (userEmbError || !userEmbData) {
          // console.warn(`User embedding não encontrado para ${userId}:`, userEmbError);
          userEmbedding = null; // ou um vetor padrão?
        } else {
          userEmbedding = userEmbData.embedding;
        }
      }

      let gameEmbedding: number[] | null = gameEmbeddingInput === undefined ? null : gameEmbeddingInput;
      if (gameEmbedding === null) { // Buscar apenas se explicitamente null ou undefined inicialmente
        const { data: gameEmbData, error: gameEmbError } = await supabase
          .from('game_embeddings')
          .select('embedding')
          .eq('game_id', game.id)
          .maybeSingle();
        if (gameEmbError || !gameEmbData) {
          // console.warn(`Game embedding não encontrado para ${game.id}:`, gameEmbError);
          gameEmbedding = null; // ou um vetor padrão?
        } else {
          gameEmbedding = gameEmbData.embedding;
        }
      }

      const similarityScore = this._calculateCosineSimilarity(userEmbedding, gameEmbedding);
      const finalScore = await this.calculateScore(game, userId, similarityScore, userCollection);
      
      // Obter preferências do usuário para gerar a razão
      const userPreferences = await RecommendationDataService.getUserPreferences(userId);

      // Gerar a razão usando o método centralizado
      const reason = this.generateDetailedReason(
        game, 
        finalScore,
        similarityScore,
        algorithm,
        userPreferences
      );

      return {
        score: finalScore,
        reason: reason
      };
    } catch (error) {
      console.error('Erro ao calcular pontuação e razão:', error);
      return {
        score: 0.5, // Score neutro
        reason: 'Recomendação baseada em popularidade geral.' // Fallback genérico
      };
    }
  }

  /**
   * Calcula a pontuação final de uma recomendação usando múltiplos fatores
   * Usa IndexGame
   */
  public async calculateScore(
    game: IndexGame,
    userId: string,
    similarityScore: number,
    userGames?: UserGame[]
  ): Promise<number> {
    try {
      // 1. Obter jogos do usuário, se não fornecidos
      if (!userGames) {
        const { data, error } = await supabase
          .from('user_games')
          .select('*')
          .eq('user_id', userId);
          
        if (error) throw error;
        userGames = data as UserGame[];
      }
      
      // 2. Calcular os componentes individuais da pontuação
      const popularityScore = await this.getPopularityScore(game.id);
      const recencyScore = this.getRecencyScore(game.year);
      const diversityScore = this.getDiversityScore(game, userGames);
      
      // 3. Combinar pontuações usando os pesos da configuração
      const finalScore = (
        similarityScore * RECOMMENDATION_CONFIG.scorerWeights.similarity +
        popularityScore * RECOMMENDATION_CONFIG.scorerWeights.popularity +
        recencyScore * RECOMMENDATION_CONFIG.scorerWeights.recency +
        diversityScore * RECOMMENDATION_CONFIG.scorerWeights.diversity
      );
      
      // 4. Normalizar para garantir que esteja entre 0 e 1
      return this.normalizeScore(finalScore);
    } catch (error) {
      console.error('Erro ao calcular pontuação:', error);
      return similarityScore; // Fallback para a similaridade original
    }
  }

  /**
   * Normaliza múltiplas pontuações para garantir distribuição adequada
   * Usa Recommendation de index.ts
   */
  public normalizeScores(recommendations: Recommendation[]): Recommendation[] {
    if (!recommendations || recommendations.length === 0) return recommendations;
    
    // Encontrar pontuação mínima e máxima
    let minScore = 1;
    let maxScore = 0;
    
    recommendations.forEach(rec => {
      minScore = Math.min(minScore, rec.score);
      maxScore = Math.max(maxScore, rec.score);
    });
    
    // Evitar divisão por zero
    const range = maxScore - minScore || 1;
    
    // Normalizar todas as pontuações
    return recommendations.map(rec => ({
      ...rec,
      score: (rec.score - minScore) / range
    }));
  }

  /**
   * Obtém pontuação de popularidade de um jogo
   */
  private async getPopularityScore(gameId: string): Promise<number> {
    try {
      // Consultar diretamente a tabela games que contém average_rating e stats
      const { data, error } = await supabase
        .from('games')
        .select('average_rating, stats')
        .eq('id', gameId)
        .single();

      // Em caso de erro na consulta, retornar score neutro
      if (error) {
        console.error(`Erro ao buscar popularidade para game ${gameId}:`, error);
        return 0.5; // Valor padrão neutro
      }

      if (!data) return 0.5; // Valor padrão se não houver dados

      // Extrair estatísticas relevantes
      const averageRating = data.average_rating ? Number(data.average_rating) : null;
      const stats = data.stats || {};

      // Calcular score com base na avaliação média e outras estatísticas
      let ratingScore = 0.5; // Valor padrão

      if (averageRating) {
        // CORREÇÃO: Normalizar avaliação para escala 0-1 (assumindo que average_rating é de 0-10)
        ratingScore = Math.max(0, Math.min(averageRating / 10, 1));
      }

      // Se houver estatísticas adicionais no campo jsonb stats, podemos usá-las
      // Exemplo: Usar contagem de avaliações (rating_count) com normalização logarítmica
      const ratingCount = stats.rating_count || 0;
      // Normaliza logaritmicamente, mapeando 1 para ~0.1, 10 para ~0.3, 100 para ~0.5, 1000 para ~0.7, 10000 para ~0.9
      const countScore = Math.min(Math.log10(Math.max(1, ratingCount)) / 4.5, 1); // Ajustado para escala ~0-1

      // Combinar os scores usando os pesos da config
      return (
        ratingScore * RECOMMENDATION_CONFIG.scorerWeights.popularityComponentWeights.rating +
        countScore * RECOMMENDATION_CONFIG.scorerWeights.popularityComponentWeights.count
      );
    } catch (error) {
      // Se ocorrer um erro inesperado durante o processamento (não na query)
      console.error(`Erro inesperado ao calcular popularidade para game ${gameId}:`, error);
      return 0.5; // Fallback seguro
    }
  }

  /**
   * Calcula pontuação baseada no ano de lançamento
   */
  private getRecencyScore(year?: number | null): number {
    if (!year) return 0.5; // Valor padrão se não houver ano (null ou undefined)
    
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    
    // Usar as faixas da configuração
    const tiers = RECOMMENDATION_CONFIG.scorerWeights.recencyTiers;
    for (const tier of tiers) {
      if (age <= tier.maxAge) {
        return tier.score;
      }
    }
    
    // Fallback se nenhuma faixa corresponder (deve ser coberto pelo maxAge: Infinity)
    return tiers[tiers.length - 1]?.score || 0.1; // Default para muito antigo
  }

  /**
   * Calcula pontuação de diversidade 
   * Usa IndexGame e UserGame[] de index.ts
   */
  private getDiversityScore(game: IndexGame, userGames: UserGame[]): number {
    // Se o usuário não tem jogos, qualquer recomendação é diversa
    if (!userGames || userGames.length === 0) return 1.0;
    
    // Coletar categorias e mecânicas dos jogos do usuário
    const userCategories = new Set<string>();
    const userMechanics = new Set<string>();
    
    userGames.forEach(userGame => {
      if (userGame.game) {
        // Adicionar categorias
        if (userGame.game.categories) {
          userGame.game.categories.forEach(cat => userCategories.add(cat));
        }
        
        // Adicionar mecânicas
        if (userGame.game.mechanics) {
          userGame.game.mechanics.forEach(mech => userMechanics.add(mech));
        }
      } else {
        // Log ou tratamento se userGame.game for inesperadamente nulo
        // console.warn(`UserGame sem dados de jogo associado: ${userGame.id}`);
      }
    });
    
    // Contar quantas categorias/mecânicas do jogo já estão na coleção
    let matchingCategories = 0;
    let matchingMechanics = 0;
    
    if (game.categories) {
      game.categories.forEach(cat => {
        if (userCategories.has(cat)) matchingCategories++;
      });
    }
    
    if (game.mechanics) {
      game.mechanics.forEach(mech => {
        if (userMechanics.has(mech)) matchingMechanics++;
      });
    }
    
    // Calcular proporção de elementos novos vs. conhecidos
    const totalCategories = (game.categories?.length || 0);
    const totalMechanics = (game.mechanics?.length || 0);
    
    // Evitar divisão por zero e dar um score base se o jogo não tiver categorias/mecânicas
    let categoryDiversity = 0.5; // Score base se o jogo não tiver categorias
    if (totalCategories > 0) {
      categoryDiversity = 1 - (matchingCategories / totalCategories);
    }
      
    let mechanicDiversity = 0.5; // Score base se o jogo não tiver mecânicas
    if (totalMechanics > 0) {
      mechanicDiversity = 1 - (matchingMechanics / totalMechanics);
    }
    
    // A lógica de pontuação por atributo não sobreposto não está implementada aqui,
    // mas sim uma média da diversidade de categorias e mecânicas.
    // A config diversityAttributeScore não se encaixa diretamente nesta lógica atual.
    // Para usar diversityAttributeScore, precisaríamos contar atributos únicos não sobrepostos
    // e multiplicar por esse score.
    // Por ora, mantendo a média da diversidade percentual de categorias e mecânicas:
    return (categoryDiversity + mechanicDiversity) / 2;
  }

  /**
   * Normaliza uma pontuação para garantir que esteja entre 0 e 1
   */
  private normalizeScore(score: number): number {
    // Garantir que a pontuação esteja entre 0 e 1
    return Math.max(0, Math.min(1, score));
  }

  /**
   * MÉTODO CENTRALIZADO PARA GERAR A RAZÃO DA RECOMENDAÇÃO
   * Usa IndexGame
   */
  private generateDetailedReason(
    game: IndexGame,
    score: number,
    similarityScore: number, 
    algorithm: RecommendationAlgorithm,
    userPreferences: { categories: Set<string>; mechanics: Set<string>; themes: Set<string> }
  ): string {
    
    // 1. Razões baseadas no Algoritmo e Similaridade
    if (algorithm === RecommendationAlgorithm.COLLABORATIVE) {
       if (score > 0.7) return `Recomendado por ser popular entre jogadores com gostos similares aos seus.`;
       return `Pode te interessar, pois outros jogadores com perfis parecidos gostaram.`;
    }

    if (algorithm === RecommendationAlgorithm.CONTENT_BASED) {
      const commonCategories = Array.isArray(game.categories) ? game.categories.filter((cat: string) => userPreferences.categories.has(cat)) : [];
      const commonMechanics = Array.isArray(game.mechanics) ? game.mechanics.filter((mech: string) => userPreferences.mechanics.has(mech)) : [];
      // const commonThemes = Array.isArray(game.themes) ? game.themes.filter((theme: string) => userPreferences.themes.has(theme)) : [];

      if (commonCategories.length > 0 && commonMechanics.length > 0) {
         return `Baseado nos seus gostos por ${commonCategories[0]} e mecânicas como ${commonMechanics[0]}.`;
      } else if (commonCategories.length > 0) {
         return `Pode gostar por ser da categoria ${commonCategories[0]}, que você curte.`;
      } else if (commonMechanics.length > 0) {
         return `Considerando seu interesse na mecânica ${commonMechanics[0]}.`;
      } else if (similarityScore > 0.6) { 
         // Se não achou categoria/mecânica mas similaridade é alta
         return 'Recomendado pela alta similaridade com jogos que você gosta.';
      } 
       // Fallback para content_based sem match claro
      return `Sugerido com base nas características gerais dos jogos que você avaliou bem.`;
    }

    if (algorithm === RecommendationAlgorithm.POPULAR) {
      if (game.average_rating && game.average_rating >= 8.0) {
         return `Um jogo muito popular e bem avaliado (${game.average_rating.toFixed(1)}/10).`;
      } else if (game.average_rating && game.average_rating >= 7.0) {
         return `Popular entre os jogadores (${game.average_rating.toFixed(1)}/10).`;
      }
      return 'Um dos jogos populares do momento.';
    }
    
    // 2. Fallback genérico (se algoritmo for HYBRID ou desconhecido)
    if (similarityScore > 0.65) {
       return 'Altamente recomendado com base no seu perfil!';
    } else if (similarityScore > 0.4) {
       return 'Pode ser interessante para você com base nos seus gostos.';
    } else if (game.average_rating && game.average_rating > 7.5) {
      return `Um jogo muito bem avaliado pela comunidade (${game.average_rating.toFixed(1)}/10)`;
    }

    return 'Recomendado para você.'; // Fallback final
  }
} 